import React, { useState, useEffect } from "react";
import PageHeader from "../../core/common/header/PageHeader";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaBriefcase,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaLock,
  FaChartLine,
  FaFileUpload,
  FaFilePdf,
  FaDownload,
  FaEye,
  FaFilter,
  FaSearch,
  FaSync,
} from "react-icons/fa";

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes

interface Application {
  id?: number;
  companyName: string;
  positionProgram: string;
  dateApplied: string;
  applicationStatus: string;
  feedbackReceived: string;
  nextAction: string;
  nextActionDue: string;
  contactPerson: string;
  contactEmail: string;
  notes: string;
  coverLetterDownloadUrl?: string;
}

interface TrackingOverview {
  studentId: number;
  studentName: string;
  studentEmail: string;
  isActivated: boolean;
  totalApplications: number;
  todayApplicationsCount: number;
  todayTargetMet: boolean;
  missedTargetDaysCount: number;
  missedTargetDates: string[];
  allApplications: Application[];
}

interface DropdownOptions {
  applicationStatus: string[];
  feedbackReceived: string[];
  nextAction: string[];
}

interface Toast {
  type: "success" | "error";
  message: string;
}

const AusbildungApplicationTracking: React.FC = () => {
  const [overview, setOverview] = useState<TrackingOverview | null>(null);
  const [dropdownOptions, setDropdownOptions] =
    useState<DropdownOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [uploadingId, setUploadingId] = useState<number | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [saving, setSaving] = useState(false);

  // Filtering & Pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [formData, setFormData] = useState<Application>({
    companyName: "",
    positionProgram: "",
    dateApplied: new Date().toISOString().split("T")[0],
    applicationStatus: "Applied",
    feedbackReceived: "No Response yet",
    nextAction: "Wait for Response",
    nextActionDue: "",
    contactPerson: "",
    contactEmail: "",
    notes: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (overview && !overview.todayTargetMet && overview.isActivated) {
      setShowNotification(true);
      const timer = setTimeout(() => setShowNotification(false), 10000);
      return () => clearTimeout(timer);
    }
  }, [overview]);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const showToast = (type: "success" | "error", message: string) => {
    setToast({ type, message });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const overviewRes = await fetch(
        `${API_BASE}/api/ausbildung-tracking/student/my-overview`,
        { credentials: "include" },
      );
      if (!overviewRes.ok) throw new Error("Failed to fetch tracking data");
      const overviewData = await overviewRes.json();

      setOverview(overviewData);

      const optionsRes = await fetch(
        `${API_BASE}/api/ausbildung-tracking/dropdown-options`,
        { credentials: "include" },
      );
      if (!optionsRes.ok) throw new Error("Failed to fetch options");
      const optionsData = await optionsRes.json();
      setDropdownOptions(optionsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleCoverLetterUpload = async (applicationId: number, file: File) => {
    if (!file || file.type !== "application/pdf") {
      showToast("error", "Please upload a PDF file.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showToast(
        "error",
        "File size must be less than 2MB. Please compress your PDF.",
      );
      return;
    }

    setUploadingId(applicationId);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      const res = await fetch(
        `${API_BASE}/api/ausbildung-tracking/student/applications/${applicationId}/cover-letter`,
        {
          method: "POST",
          credentials: "include",
          body: formDataUpload,
        },
      );

      if (res.status === 413) {
        throw new Error("File too large. Maximum size is 2MB.");
      }

      if (!res.ok) throw new Error("Failed to upload cover letter");

      await fetchData();

      showToast("success", "Cover letter uploaded successfully!");
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const handleAddNew = () => {
    if (!overview?.isActivated) {
      showToast(
        "error",
        "You are not authorized to add applications. Please contact admin.",
      );
      return;
    }
    setIsAddingNew(true);
    setFormData({
      companyName: "",
      positionProgram: "",
      dateApplied: new Date().toISOString().split("T")[0],
      applicationStatus: "Applied",
      feedbackReceived: "No Response yet",
      nextAction: "Wait for Response",
      nextActionDue: "",
      contactPerson: "",
      contactEmail: "",
      notes: "",
    });
  };

  const handleEdit = (app: Application) => {
    if (!overview?.isActivated) {
      showToast(
        "error",
        "You are not authorized to edit applications. Please contact admin.",
      );
      return;
    }
    setEditingId(app.id || null);
    setFormData(app);
  };

  const handleSave = async () => {
    // Prevent multiple submissions
    if (saving) return;

    try {
      setSaving(true);

      if (isAddingNew) {
        const res = await fetch(
          `${API_BASE}/api/ausbildung-tracking/student/applications`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(formData),
          },
        );
        if (!res.ok) throw new Error("Failed to create application");
        const createdApp = await res.json();

        const fileInput = document.getElementById(
          "coverLetterInput",
        ) as HTMLInputElement;
        const file = fileInput?.files?.[0];
        if (file && createdApp.id) {
          await handleCoverLetterUpload(createdApp.id, file);
        }

        showToast("success", "Application created successfully!");
      } else if (editingId) {
        const res = await fetch(
          `${API_BASE}/api/ausbildung-tracking/student/applications/${editingId}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(formData),
          },
        );
        if (!res.ok) throw new Error("Failed to update application");

        const fileInput = document.getElementById(
          "coverLetterInput",
        ) as HTMLInputElement;
        const file = fileInput?.files?.[0];
        if (file && editingId) {
          await handleCoverLetterUpload(editingId, file);
        }

        showToast("success", "Application updated successfully!");
      }
      await fetchData();
      handleCancel();
    } catch (err) {
      showToast("error", err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!overview?.isActivated) {
      showToast(
        "error",
        "You are not authorized to delete applications. Please contact admin.",
      );
      return;
    }
    if (!window.confirm("Are you sure you want to delete this application?"))
      return;
    try {
      const res = await fetch(
        `${API_BASE}/api/ausbildung-tracking/student/applications/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        },
      );
      if (!res.ok) throw new Error("Failed to delete application");
      showToast("success", "Application deleted successfully!");
      await fetchData();
    } catch (err) {
      showToast(
        "error",
        err instanceof Error ? err.message : "Failed to delete",
      );
    }
  };

  const handleCancel = () => {
    setIsAddingNew(false);
    setEditingId(null);
    setFormData({
      companyName: "",
      positionProgram: "",
      dateApplied: new Date().toISOString().split("T")[0],
      applicationStatus: "Applied",
      feedbackReceived: "No Response yet",
      nextAction: "Wait for Response",
      nextActionDue: "",
      contactPerson: "",
      contactEmail: "",
      notes: "",
    });
  };

  const handleInputChange = (field: keyof Application, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getDailyProgressPercentage = () => {
    if (!overview) return 0;
    const percentage = (overview.todayApplicationsCount / 2) * 100;
    return Math.min(Math.max(percentage, 0), 100);
  };

  // Filter applications
  const getFilteredApplications = () => {
    if (!overview) return [];

    let filtered = overview.allApplications;

    // Search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (app) =>
          app.companyName.toLowerCase().includes(search) ||
          app.positionProgram.toLowerCase().includes(search) ||
          app.contactPerson?.toLowerCase().includes(search) ||
          app.notes?.toLowerCase().includes(search),
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (app) => app.applicationStatus === statusFilter,
      );
    }

    return filtered;
  };

  const filteredApplications = getFilteredApplications();

  // Pagination calculations
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedApplications = filteredApplications.slice(
    startIndex,
    endIndex,
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <FaSync className="animate-spin h-12 w-12 text-[#009fe3] mx-auto mb-4" />
          <p className="text-lg font-semibold text-gray-700 mb-2">
            Loading application tracking...
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-600 mb-2">📊 Fetching your data:</p>
            <ul className="text-xs text-gray-500 space-y-1">
              <li>✓ Loading applications...</li>
              <li>✓ Calculating statistics...</li>
              <li>✓ Preparing dashboard...</li>
            </ul>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            This usually takes 1-2 seconds
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <FaExclamationTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="bg-[#009fe3] text-white px-4 py-2 rounded-md hover:bg-[#007cb5]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in">
          <div
            className={`rounded-lg shadow-lg p-4 min-w-[300px] ${
              toast.type === "success"
                ? "bg-green-50 border-l-4 border-green-500"
                : "bg-red-50 border-l-4 border-red-500"
            }`}
          >
            <div className="flex items-center">
              {toast.type === "success" ? (
                <FaCheckCircle
                  className="text-green-600 mr-3 flex-shrink-0"
                  size={20}
                />
              ) : (
                <FaExclamationTriangle
                  className="text-red-600 mr-3 flex-shrink-0"
                  size={20}
                />
              )}
              <p
                className={`text-sm font-medium ${
                  toast.type === "success" ? "text-green-800" : "text-red-800"
                }`}
              >
                {toast.message}
              </p>
              <button
                onClick={() => setToast(null)}
                className={`ml-auto ${
                  toast.type === "success"
                    ? "text-green-600 hover:text-green-800"
                    : "text-red-600 hover:text-red-800"
                }`}
              >
                <FaTimes />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <PageHeader
        icon={<FaBriefcase />}
        title="Application Tracking"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/student/student-dashboard" },
          { label: "Application Tracking" },
        ]}
      />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Target Not Met Notification */}
        {showNotification && overview && !overview.todayTargetMet && (
          <div className="mb-6 bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md shadow-sm">
            <div className="flex items-center">
              <FaExclamationTriangle
                className="text-yellow-600 mr-3 flex-shrink-0"
                size={20}
              />
              <div>
                <p className="text-yellow-800 font-semibold">
                  Daily Target Not Met!
                </p>
                <p className="text-yellow-700 text-sm">
                  You have submitted {overview.todayApplicationsCount} out of 2
                  required applications today.
                </p>
              </div>
              <button
                onClick={() => setShowNotification(false)}
                className="ml-auto text-yellow-600 hover:text-yellow-800"
              >
                <FaTimes />
              </button>
            </div>
          </div>
        )}

        {/* Not Activated Warning */}
        {overview && !overview.isActivated && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md shadow-sm">
            <div className="flex items-center">
              <FaLock className="text-red-600 mr-3 flex-shrink-0" size={20} />
              <div>
                <p className="text-red-800 font-semibold">
                  Editing Not Activated
                </p>
                <p className="text-red-700 text-sm">
                  Your application tracking is currently in read-only mode.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        {overview && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <FaBriefcase className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Total Applications
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {overview.totalApplications}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <FaCalendarAlt className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Today's Applications
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {overview.todayApplicationsCount} / 2
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                {overview.todayTargetMet ? (
                  <FaCheckCircle className="h-8 w-8 text-green-600" />
                ) : (
                  <FaExclamationTriangle className="h-8 w-8 text-yellow-600" />
                )}
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Today's Target
                  </p>
                  <p
                    className={`text-2xl font-semibold ${
                      overview.todayTargetMet
                        ? "text-green-600"
                        : "text-yellow-600"
                    }`}
                  >
                    {overview.todayTargetMet ? "Met" : "Not Met"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <FaChartLine className="h-8 w-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">
                    Missed Target Days
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {overview.missedTargetDaysCount}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Daily Progress Bar */}
        {overview && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Today's Progress
              </h3>
              <span
                className={`text-sm font-semibold ${
                  overview.todayTargetMet ? "text-green-600" : "text-yellow-600"
                }`}
              >
                {overview.todayApplicationsCount} / 2 Applications
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-6 overflow-hidden">
              <div
                className={`h-6 rounded-full transition-all duration-500 flex items-center justify-end pr-2 ${
                  overview.todayTargetMet ? "bg-green-500" : "bg-yellow-500"
                }`}
                style={{ width: `${getDailyProgressPercentage()}%` }}
              >
                {getDailyProgressPercentage() > 15 && (
                  <span className="text-xs font-semibold text-white">
                    {Math.round(getDailyProgressPercentage())}%
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {overview.todayTargetMet
                ? "Great job! You've met your daily target."
                : `Add ${
                    2 - overview.todayApplicationsCount
                  } more application(s) to meet your daily target.`}
            </p>
          </div>
        )}

        {/* Applications Section */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              All Applications
            </h3>
            <button
              onClick={handleAddNew}
              disabled={!overview?.isActivated}
              className={`flex items-center gap-2 px-4 py-2 rounded-md ${
                overview?.isActivated
                  ? "bg-[#009fe3] text-white hover:bg-[#007cb5]"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              <FaPlus /> Add New Application
            </button>
          </div>

          {/* Search & Filters */}
          {!isAddingNew && !editingId && (
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Search */}
                <div className="md:col-span-2">
                  <div className="relative">
                    <FaSearch className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by company, position, contact, or notes..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Status Filter */}
                <div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Statuses</option>
                    {dropdownOptions?.applicationStatus.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Results Info */}
              <div className="mt-3 flex items-center justify-between text-sm text-gray-600 flex-wrap gap-2">
                <div>
                  Showing {startIndex + 1}-
                  {Math.min(endIndex, filteredApplications.length)} of{" "}
                  {filteredApplications.length} application(s)
                  {(searchTerm || statusFilter !== "all") && (
                    <span className="text-gray-500">
                      {" "}
                      (filtered from {overview?.allApplications.length || 0})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm">Per page:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Add/Edit Form Modal */}
          {(isAddingNew || editingId) && (
            <div className="bg-blue-50 border-b-2 border-blue-200 p-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">
                {isAddingNew ? "Add New Application" : "Edit Application"}
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={(e) =>
                      handleInputChange("companyName", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter company name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Position / Program *
                  </label>
                  <input
                    type="text"
                    value={formData.positionProgram}
                    onChange={(e) =>
                      handleInputChange("positionProgram", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter position"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Applied *
                  </label>
                  <input
                    type="date"
                    value={formData.dateApplied}
                    onChange={(e) =>
                      handleInputChange("dateApplied", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Application Status
                  </label>
                  <select
                    value={formData.applicationStatus}
                    onChange={(e) =>
                      handleInputChange("applicationStatus", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {dropdownOptions?.applicationStatus.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Feedback Received
                  </label>
                  <select
                    value={formData.feedbackReceived}
                    onChange={(e) =>
                      handleInputChange("feedbackReceived", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {dropdownOptions?.feedbackReceived.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Next Action
                  </label>
                  <select
                    value={formData.nextAction}
                    onChange={(e) =>
                      handleInputChange("nextAction", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {dropdownOptions?.nextAction.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Next Action Due
                  </label>
                  <input
                    type="date"
                    value={formData.nextActionDue}
                    onChange={(e) =>
                      handleInputChange("nextActionDue", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person
                  </label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) =>
                      handleInputChange("contactPerson", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Contact name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) =>
                      handleInputChange("contactEmail", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="email@example.com"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange("notes", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Add any additional notes"
                    rows={2}
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cover Letter (PDF - Max 2MB)
                  </label>
                  <input
                    type="file"
                    accept="application/pdf"
                    id="coverLetterInput"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isAddingNew
                      ? "File will be uploaded after saving the application"
                      : "File will be uploaded immediately when you save"}
                  </p>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className={`flex items-center gap-2 px-6 py-2 rounded-md font-medium ${
                    saving
                      ? "bg-gray-400 text-gray-200 cursor-not-allowed"
                      : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <FaSave /> Save Application
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 font-medium disabled:opacity-50"
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            </div>
          )}

          {/* Applications List - KEEPING ALL ORIGINAL COLUMNS AND DOWNLOAD BUTTON */}
          <div className="divide-y divide-gray-200">
            {paginatedApplications.map((app) => (
              <div
                key={app.id}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900">
                      {app.companyName}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {app.positionProgram}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(app)}
                      disabled={!overview?.isActivated}
                      className={`p-2 rounded-md ${
                        overview?.isActivated
                          ? "text-blue-600 hover:bg-blue-50"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                      title="Edit"
                    >
                      <FaEdit size={18} />
                    </button>
                    <button
                      onClick={() => app.id && handleDelete(app.id)}
                      disabled={!overview?.isActivated}
                      className={`p-2 rounded-md ${
                        overview?.isActivated
                          ? "text-red-600 hover:bg-red-50"
                          : "text-gray-400 cursor-not-allowed"
                      }`}
                      title="Delete"
                    >
                      <FaTrash size={18} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Date Applied</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(app.dateApplied).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Status</p>
                    <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {app.applicationStatus}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Feedback</p>
                    <p className="text-sm font-medium text-gray-900">
                      {app.feedbackReceived}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Next Action</p>
                    <p className="text-sm font-medium text-gray-900">
                      {app.nextAction}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Next Action Due
                    </p>
                    <p className="text-sm font-medium text-gray-900">
                      {app.nextActionDue
                        ? new Date(app.nextActionDue).toLocaleDateString()
                        : "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Contact Person</p>
                    <p className="text-sm font-medium text-gray-900">
                      {app.contactPerson || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Contact Email</p>
                    <p className="text-sm font-medium text-gray-900">
                      {app.contactEmail || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Cover Letter</p>
                    <div className="flex flex-wrap gap-2 items-center">
                      {app.coverLetterDownloadUrl ? (
                        <>
                          <a
                            href={app.coverLetterDownloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                          >
                            <FaEye /> Preview
                          </a>
                          <a
                            href={app.coverLetterDownloadUrl}
                            download
                            className="inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium"
                          >
                            <FaDownload /> Download
                          </a>
                          {overview?.isActivated && (
                            <label className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 font-medium cursor-pointer">
                              <FaFileUpload /> Replace
                              <input
                                type="file"
                                accept="application/pdf"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file && app.id) {
                                    handleCoverLetterUpload(app.id, file);
                                    e.currentTarget.value = "";
                                  }
                                }}
                                className="hidden"
                                disabled={uploadingId === app.id}
                              />
                            </label>
                          )}
                        </>
                      ) : (
                        overview?.isActivated && (
                          <label className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium cursor-pointer">
                            <FaFileUpload /> Upload PDF
                            <input
                              type="file"
                              accept="application/pdf"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file && app.id) {
                                  handleCoverLetterUpload(app.id, file);
                                  e.currentTarget.value = "";
                                }
                              }}
                              className="hidden"
                              disabled={uploadingId === app.id}
                            />
                          </label>
                        )
                      )}
                      {uploadingId === app.id && (
                        <span className="text-xs text-yellow-600 flex items-center gap-1">
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-600"></div>
                          Uploading...
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {app.notes && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-md">
                    <p className="text-xs text-gray-500 mb-1">Notes</p>
                    <p className="text-sm text-gray-700">{app.notes}</p>
                  </div>
                )}
              </div>
            ))}

            {/* Empty State */}
            {paginatedApplications.length === 0 && !isAddingNew && (
              <div className="p-12 text-center text-gray-500">
                <FaBriefcase className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  {searchTerm || statusFilter !== "all"
                    ? "No matching applications found"
                    : "No applications yet"}
                </p>
                <p className="text-sm">
                  {searchTerm || statusFilter !== "all" ? (
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setStatusFilter("all");
                      }}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Clear filters
                    </button>
                  ) : (
                    "Click 'Add New Application' to start tracking"
                  )}
                </p>
              </div>
            )}
          </div>

          {/* IMPROVED Pagination Controls - Same as Admin Dashboard */}
          {filteredApplications.length > 0 && !isAddingNew && !editingId && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    First
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                </div>

                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </span>
                  {totalPages <= 10 ? (
                    // Show all pages if 10 or less
                    Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          className={`px-3 py-2 border rounded-md text-sm font-medium ${
                            currentPage === page
                              ? "bg-[#009fe3] text-white border-[#009fe3]"
                              : "border-gray-300 text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {page}
                        </button>
                      ),
                    )
                  ) : (
                    // Show condensed pagination for more than 10 pages
                    <>
                      {currentPage > 3 && (
                        <span className="text-gray-500">...</span>
                      )}
                      {Array.from({ length: 5 }, (_, i) => {
                        const page = currentPage - 2 + i;
                        if (page < 1 || page > totalPages) return null;
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 border rounded-md text-sm font-medium ${
                              currentPage === page
                                ? "bg-[#009fe3] text-white border-[#009fe3]"
                                : "border-gray-300 text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      {currentPage < totalPages - 2 && (
                        <span className="text-gray-500">...</span>
                      )}
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Last
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <style>{`
        @keyframes slide-in {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AusbildungApplicationTracking;
